import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Form, Button, Row, Col } from "react-bootstrap";
import {
  getRecoDocTypeData,
  manualBankReconciliation,
} from "../Api/Reconciliation";
import {
  getCustomerData,
  getAllCustomerExternalIds,
} from "../Api/CustomerMaster";
import { getProjectDetails } from "../Api/ProjectMaster";
import { fetchItems } from "../Api/ViewItems";
import { fetchProjectResourceMappings } from "../Api/ViewProjectResourceMapping";
import { fetchAcitDataForAll } from "../Api/AchdDataWithUTR";
import { getAllCompanyCodes } from "../Api/CompanyLocationMaster";

const PaymentReconciliation = () => {
  const location = useLocation();
  const { statement } = location.state || {};
  const navigate = useNavigate();
  const { monthId, houseBankId } = useParams();

  const [formData, setFormData] = useState({
    transferType: "", // ✅ add this
    transferFromCompanyCode: "", // ✅ used later in payload
    transferToCompanyCode: "", // ✅ used later in payload
    currency: "INR", // ✅ default value
    statementAmount: statement?.transactionAmount || "",
    selectedAmount: "",
    balanceAmount: "",
  });

  const [filters, setFilters] = useState({
    paymentType: "",
    clientCode: "",
    projectCode: "",
    resourceCode: "",
    vendorCode: "",
    materialCode: "",
  });

  // Options state
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [clientCodes, setClientCodes] = useState([]);
  const [vendorCodes, setVendorCodes] = useState([]);
  const [materialCodes, setMaterialCodes] = useState([]);
  const [projectCodes, setProjectCodes] = useState([]);
  const [resourceCodes, setResourceCodes] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [companies, setCompanies] = useState([]);

  // ACIT Data
  const [acitData, setAcitData] = useState([]);
  const [filteredAcitData, setFilteredAcitData] = useState([]);

  // Fetch company codes
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await getAllCompanyCodes();
        setCompanies(res || []); // ensure array
      } catch (err) {
        console.error("Error fetching company codes:", err);
      }
    };
    fetchCompanies();
  }, []);

  useEffect(() => {
    // Fetch Payment Types
    getRecoDocTypeData()
      .then((res) => {
        if (Array.isArray(res)) {
          const mapped = res.map((item) => ({
            value: item.bankrecodoctypeid,
            label: `${item.docType} - ${item.matchingDocType}`,
          }));
          setPaymentTypes(mapped);
        }
      })
      .catch((err) => console.error("Error fetching Payment Types:", err));

    // Fetch Clients & Vendors
    getCustomerData()
      .then((res) => {
        if (Array.isArray(res)) {
          const clients = res
            .filter((c) => c.type === "Customer")
            .map((c) => ({
              value: c.code,
              label: `${c.code} - ${c.name}`,
            }));
          const vendors = res
            .filter((c) => c.type === "Vendor")
            .map((c) => ({
              value: c.code,
              label: `${c.code} - ${c.name}`,
            }));
          setClientCodes(clients);
          setVendorCodes(vendors);
        }
      })
      .catch((err) => console.error("Error fetching Customers/Vendors:", err));

    // Fetch Materials
    fetchItems()
      .then((res) => {
        if (Array.isArray(res)) {
          const mapped = res.map((item) => ({
            value: item.itemCodeId,
            label: `${item.itemCode} - ${item.itemName}`,
          }));
          setMaterialCodes(mapped);
        }
      })
      .catch((err) => console.error("Error fetching Items:", err));

    // Fetch ACIT Data
    fetchAcitDataForAll()
      .then((res) => {
        if (Array.isArray(res)) {
          setAcitData(res);
          setFilteredAcitData(res);
          const filteredRes = res.filter((item) => item.clearingStatus == null);
          setAcitData(filteredRes);
          setFilteredAcitData(filteredRes);
        }
      })
      .catch((err) => console.error("Error fetching ACIT Data:", err));
  }, []);

  // When client code changes, fetch project codes
  useEffect(() => {
    if (!filters.clientCode) {
      setProjectCodes([]);
      setFilters((prev) => ({ ...prev, projectCode: "" }));
      return;
    }

    const fetchProjects = async () => {
      try {
        const externalIds = await getAllCustomerExternalIds(filters.clientCode);

        const clientMatch = externalIds.find(
          (id) => id.customerCode === filters.clientCode
        );

        if (clientMatch) {
          const extSysCode = clientMatch.externalSystemCode;

          const projects = await getProjectDetails();
          console.log("Fetched Projects:", projects);
          if (Array.isArray(projects)) {
            const matchedProjects = projects
              .filter((p) => p.prClient === extSysCode)
              .map((p) => ({
                value: p.prozmaPrId,
                label: `${p.prozmaPrId} - ${p.prTitle}`,
              }));

            setProjectCodes(matchedProjects);
          }
        } else {
          setProjectCodes([]);
        }
      } catch (err) {
        console.error("Error fetching projects:", err);
      }
    };

    fetchProjects();
  }, [filters.clientCode]);

  // When Project Code changes, fetch related resources
  useEffect(() => {
    if (!filters.projectCode) {
      setResourceCodes([]);
      setFilters((prev) => ({ ...prev, resourceCode: "" }));
      return;
    }

    const fetchResources = async () => {
      try {
        const res = await fetchProjectResourceMappings();
        if (Array.isArray(res)) {
          const filtered = res
            .filter((r) => r.projectCode === filters.projectCode)
            .map((r) => ({
              value: r.resource,
              label: `${r.resource} - ${r.projectName}`,
            }));
          setResourceCodes(filtered);
        }
      } catch (err) {
        console.error("Error fetching project resources:", err);
      }
    };

    fetchResources();
  }, [filters.projectCode]);

  // Apply filters to ACIT Data
  useEffect(() => {
    let filtered = [...acitData];

    if (filters.clientCode) {
      filtered = filtered.filter((d) => d.clientWbsCode === filters.clientCode);
    }
    if (filters.projectCode) {
      filtered = filtered.filter(
        (d) => d.projectWbsCode === filters.projectCode
      );
    }
    if (filters.resourceCode) {
      filtered = filtered.filter(
        (d) => d.resourceWbsCode === filters.resourceCode
      );
    }
    if (filters.vendorCode) {
      filtered = filtered.filter((d) => d.vendorWbsCode === filters.vendorCode);
    }
    if (filters.materialCode) {
      filtered = filtered.filter(
        (d) => d.materialWbsCode === filters.materialCode
      );
    }

    setFilteredAcitData(filtered);
  }, [filters, acitData]);

  useEffect(() => {
    const totalSelected = selectedRows.reduce(
      (sum, row) => sum + parseFloat(row.amount || 0),
      0
    );
    const balance = (parseFloat(formData.statementAmount) || 0) - totalSelected;

    setFormData((prev) => ({
      ...prev,
      selectedAmount: totalSelected.toFixed(2),
      balanceAmount: balance.toFixed(2),
    }));
  }, [selectedRows, formData.statementAmount]);

  // Handle checkbox toggle
  const handleCheckboxChange = (row, isChecked) => {
    if (isChecked) {
      setSelectedRows((prev) => [...prev, row]);
    } else {
      setSelectedRows((prev) => prev.filter((r) => r.id !== row.id));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Replace handleCheckAmount function with refresh functionality
  const handleRefresh = () => {
    window.location.reload();
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();

  //   try {
  //     const currentYear = new Date().getFullYear();
  //     const currentMonthMinus2 = new Date().getMonth() - 1; // adjust as per your logic

  //     const payload = {
  //       statementId: statement.statementId,
  //       docfy: String(currentYear),
  //       recoAmount: statement.transactionAmount,
  //       recoCurrency: "INR",
  //       document_Type: formData.transferType,
  //       manualAchdData: [
  //         {
  //           docfy: Number(currentYear),
  //           docfm: Number(currentMonthMinus2),
  //           doccc: parseInt(
  //             statement.creditDebit === "DR"
  //               ? formData.transferFromCompanyCode
  //               : formData.transferToCompanyCode
  //           ),
  //           postingDate: statement.transactionPostedDate,
  //           documentDate: statement.valueDate,
  //           refNo: statement.statementId,
  //           enteredBy: "2",
  //           documentType: formData.transferType,
  //           approvalStatus: null,
  //           utrNumber: null,
  //           utrUpdatedBy: null,
  //           utrUpdatedOn: null,
  //           weekNumber: null,
  //           approvalLevel: null,
  //         },
  //       ],
  //       manualAcitData: [
  //         {
  //           docfy: Number(currentYear),
  //           docfm: Number(currentMonthMinus2),
  //           doccc: parseInt(
  //             statement.creditDebit === "DR"
  //               ? formData.transferFromCompanyCode
  //               : formData.transferToCompanyCode
  //           ),
  //           docitem: "",
  //           postingkey: "",
  //           gl: "",
  //           amount: statement.transactionAmount,
  //           currency: formData.currency,
  //           debitcredit: statement.creditDebit === "DR" ? "D" : "C",
  //           reportinG_WBS: "",
  //           transaction_Code: formData.transferType,
  //           client_WbsCode: null,
  //           resource_WbsCode: "",
  //           project_WbsCode: null,
  //           vendor_WbsCode: null,
  //           resource_dummy: "",
  //           material_WbsCode: null,
  //           bank: "",
  //           sales_InvoiceNo: null,
  //           cost_Center: null,
  //           account_Type: "",
  //           item_number_ref: null,
  //           clearing_status: "1",
  //         },
  //       ],
  //     };

  //     console.log("Final Payload:", payload);

  //     const res = await manualBankReconciliation(payload);
  //     console.log("Save Response:", res);

  //     alert("Reconciliation saved successfully!");
  //   } catch (err) {
  //     console.error("Error saving reconciliation:", err);
  //     alert("Failed to save reconciliation");
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Pick doccc from first selected row
    const selectedDoccc =
      selectedRows.length > 0 ? parseInt(selectedRows[0].doccc) : null;

    try {
      const currentYear = new Date().getFullYear();
      const currentMonthMinus2 = new Date().getMonth() - 1; // adjust as per your logic

      // Create manualAcitData array from all selected rows
      // const manualAcitData = selectedRows.map((row) => ({
      //   docfy: Number(currentYear),
      //   docfm: Number(currentMonthMinus2),
      //   doccc: parseInt(
      //     statement.creditDebit === "DR"
      //       ? formData.transferFromCompanyCode
      //       : formData.transferToCompanyCode
      //   ),
      //   docitem: row.docitem || "",
      //   postingkey: row.postingkey || "",
      //   gl: row.gl || "",
      //   amount: String(row.amount),
      //   currency: row.currencycode || formData.currency,
      //   debitcredit:
      //     row.debitcredit || (statement.creditDebit === "DR" ? "D" : "C"),
      //   reportinG_WBS: row.reportingWbs || "",
      //   transaction_Code: formData.transferType,
      //   client_WbsCode: row.clientWbsCode || null,
      //   resource_WbsCode: row.resourceWbsCode || "",
      //   project_WbsCode: row.projectWbsCode || null,
      //   vendor_WbsCode: row.vendorWbsCode || null,
      //   resource_dummy: "",
      //   material_WbsCode: row.materialWbsCode || null,
      //   bank: row.bank || "",
      //   sales_InvoiceNo: null,
      //   cost_Center: null,
      //   account_Type: "",
      //   item_number_ref: null,
      //   clearing_status: "1",
      // }));

      const manualAcitData = selectedRows.map((row) => ({
        docfy: Number(currentYear),
        docfm: Number(currentMonthMinus2),
        doccc: selectedDoccc,
        docitem: row.docitem || "",
        postingkey: row.postingkey || "",
        gl: row.gl || "",
        amount: String(row.amount),
        currency: row.currencycode || formData.currency,
        debitcredit:
          row.debitcredit || (statement.creditDebit === "DR" ? "D" : "C"),
        reportinG_WBS: row.reportingWbs || "",
        transaction_Code: formData.transferType,
        client_WbsCode: row.clientWbsCode || null,
        resource_WbsCode: row.resourceWbsCode || "",
        project_WbsCode: row.projectWbsCode || null,
        vendor_WbsCode: row.vendorWbsCode || null,
        resource_dummy: "",
        material_WbsCode: row.materialWbsCode || null,
        bank: row.bank || "",
        sales_InvoiceNo: null,
        cost_Center: null,
        account_Type: "",
        item_number_ref: null,
        clearing_status: "1",
      }));

      // 🔹 Always add one row for the statement itself
      manualAcitData.push({
        docfy: Number(currentYear),
        docfm: Number(currentMonthMinus2),
        doccc: selectedDoccc,
        docitem: "",
        postingkey: "",
        gl: "",
        amount: formData.statementAmount,
        currency: formData.currency,
        debitcredit: statement.creditDebit === "DR" ? "D" : "C",
        reportinG_WBS: "",
        transaction_Code: formData.transferType,
        client_WbsCode: null,
        resource_WbsCode: "",
        project_WbsCode: null,
        vendor_WbsCode: null,
        resource_dummy: "",
        material_WbsCode: null,
        bank: statement.bankName || "",
        sales_InvoiceNo: null,
        cost_Center: null,
        account_Type: "",
        item_number_ref: null,
        clearing_status: "1",
      });

      // Add manual bank entry if no ACIT rows are selected
      if (manualAcitData.length === 0) {
        manualAcitData.push({
          docfy: Number(currentYear),
          docfm: Number(currentMonthMinus2),
          doccc: selectedDoccc,
          docitem: "",
          postingkey: "",
          gl: "",
          amount: formData.statementAmount,
          currency: formData.currency,
          debitcredit: statement.creditDebit === "DR" ? "D" : "C",
          reportinG_WBS: "",
          transaction_Code: formData.transferType,
          client_WbsCode: null,
          resource_WbsCode: "",
          project_WbsCode: null,
          vendor_WbsCode: null,
          resource_dummy: "",
          material_WbsCode: null,
          bank: "",
          sales_InvoiceNo: null,
          cost_Center: null,
          account_Type: "",
          item_number_ref: null,
          clearing_status: "1",
        });
      }

      const payload = {
        statementId: statement.statementId,
        docfy: String(currentYear),
        recoAmount: statement.transactionAmount,
        recoCurrency: "INR",
        document_Type: formData.transferType,
        manualAchdData: [
          {
            docfy: Number(currentYear),
            docfm: Number(currentMonthMinus2),
            doccc: selectedDoccc,
            postingDate: statement.transactionPostedDate,
            documentDate: statement.valueDate,
            refNo: statement.statementId,
            enteredBy: "2",
            documentType: formData.transferType,
            approvalStatus: null,
            utrNumber: null,
            utrUpdatedBy: null,
            utrUpdatedOn: null,
            weekNumber: null,
            approvalLevel: null,
          },
        ],
        manualAcitData: manualAcitData, // Use the array created from selected rows + manual entry
      };

      console.log("Final Payload:", payload);

      const res = await manualBankReconciliation(payload);
      console.log("Save Response:", res);

      alert("Reconciliation saved successfully!");
      navigate(`/manual-Bank-reconciliation/${monthId}/${houseBankId}`);
    } catch (err) {
      console.error("Error saving reconciliation:", err);
      alert("Failed to save reconciliation");
    }
  };

  return (
    <div className="container mt-4">
      <h4>Payment Reconciliation</h4>
      {statement ? (
        <>
          {/* Statement Card */}
          <div className="card mt-3">
            <div className="card-header">
              <h5>Statement Details</h5>
            </div>
            <div className="card-body">
              <table className="table table-bordered">
                <tbody>
                  <tr>
                    <th>Statement ID</th>
                    <td>{statement.statementId}</td>
                  </tr>
                  <tr>
                    <th>House Bank ID</th>
                    <td>{statement.housebankId}</td>
                  </tr>
                  <tr>
                    <th>Transaction ID</th>
                    <td>{statement.transactionId}</td>
                  </tr>
                  <tr>
                    <th>Narration</th>
                    <td>{statement.narration}</td>
                  </tr>
                  <tr>
                    <th>Bank Name</th>
                    <td>{statement.bankName}</td>
                  </tr>
                  <tr>
                    <th>Credit/Debit</th>
                    <td>{statement.creditDebit}</td>
                  </tr>
                  <tr>
                    <th>Account Number</th>
                    <td>{statement.accountNumber}</td>
                  </tr>
                  <tr>
                    <th>Amount</th>
                    <td>{statement.transactionAmount}</td>
                  </tr>
                  <tr>
                    <th>Transaction Date</th>
                    <td>
                      {new Date(
                        statement.transactionPostedDate
                      ).toLocaleDateString()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Form  Section */}
          <Form className="mt-4" onSubmit={handleSubmit}>
            <h5>Reconciliation Form</h5>

            <Form.Group className="mb-3">
              <Form.Label>Transfer Type</Form.Label>
              <Form.Select
                name="transferType"
                value={formData.transferType}
                onChange={handleChange}
                required
              >
                <option value="I4">I4 - Internal</option>
                <option value="I2">I2 - External</option>
              </Form.Select>
            </Form.Group>
            {/* <Form.Group className="mb-3">
              <Form.Label>Transfer From Company Code</Form.Label>
              <Form.Select
                name="transferFromCompanyCode"
                value={formData.transferFromCompanyCode}
                onChange={handleChange}
                required
              >
                <option value="">Select Company</option>
                {companies.map((c) => (
                  <option key={c.companyCode} value={c.companyCode}>
                    {c.companyCode} - {c.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group> */}
            {/* <Form.Group className="mb-3">
              <Form.Label>Transfer To Company Code</Form.Label>
              <Form.Select
                name="transferToCompanyCode"
                value={formData.transferToCompanyCode}
                onChange={handleChange}
                required
              >
                <option value="">Select Company</option>
                {companies.map((c) => (
                  <option key={c.companyCode} value={c.companyCode}>
                    {c.companyCode} - {c.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group> */}
            <Form.Group className="mb-3">
              <Form.Label>Statement Amount</Form.Label>
              <Form.Control
                type="number"
                name="statementAmount"
                value={formData.statementAmount}
                onChange={handleChange}
                readOnly
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Selected Amount</Form.Label>
              <Form.Control
                type="number"
                name="selectedAmount"
                value={formData.selectedAmount}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Balance Amount</Form.Label>
              <Form.Control
                type="number"
                name="balanceAmount"
                value={formData.balanceAmount}
                onChange={handleChange}
                readOnly
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Currency</Form.Label>
              <Form.Select
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                required
              >
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </Form.Select>
            </Form.Group>

            {/* Buttons */}
            <div className="d-flex gap-2 mb-4">
              <Button variant="secondary" type="button" onClick={handleRefresh}>
                Check Amount
              </Button>
              <Button
                variant="primary"
                type="submit"
                // disabled={formData.balanceAmount !== "0.00"}
              >
                Save
              </Button>
            </div>

            {/* Filter Area */}
            {/* Filter Area */}
            <div className="card p-3">
              <h5>Filter Area</h5>
              <Row className="g-3">
                {/* Payment Type */}
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Payment Type</Form.Label>
                    <Form.Select
                      name="paymentType"
                      value={filters.paymentType}
                      onChange={handleFilterChange}
                    >
                      <option value="">Select Payment Type</option>
                      {paymentTypes.map((pt) => (
                        <option key={pt.value} value={pt.value}>
                          {pt.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                {/* Client Code */}
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Client Code</Form.Label>
                    <Form.Select
                      name="clientCode"
                      value={filters.clientCode}
                      onChange={handleFilterChange}
                    >
                      <option value="">Select Client</option>
                      {clientCodes.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                {/* Project Code */}
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Project Code</Form.Label>
                    <Form.Select
                      name="projectCode"
                      value={filters.projectCode}
                      onChange={handleFilterChange}
                    >
                      <option value="">Select Project</option>
                      {projectCodes.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                {/* Resource Code */}
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Resource Code</Form.Label>
                    <Form.Select
                      name="resourceCode"
                      value={filters.resourceCode}
                      onChange={handleFilterChange}
                    >
                      <option value="">Select Resource</option>
                      {resourceCodes.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                {/* Vendor Code */}
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Vendor Code</Form.Label>
                    <Form.Select
                      name="vendorCode"
                      value={filters.vendorCode}
                      onChange={handleFilterChange}
                    >
                      <option value="">Select Vendor</option>
                      {vendorCodes.map((v) => (
                        <option key={v.value} value={v.value}>
                          {v.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                {/* Material Code */}
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Material Code</Form.Label>
                    <Form.Select
                      name="materialCode"
                      value={filters.materialCode}
                      onChange={handleFilterChange}
                    >
                      <option value="">Select Material</option>
                      {materialCodes.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </Form>

          {/* 🔹 ACIT Table Section */}
          <div className="card mt-4">
            <div className="card-header">
              <h5>ACIT Table</h5>
            </div>
            <div className="card-body">
              {filteredAcitData.length > 0 ? (
                <table className="table table-bordered table-striped">
                  <thead>
                    <tr>
                      <th>Select</th>
                      <th>Doc No</th>
                      <th>FY</th>
                      <th>doccc</th>
                      <th>Item</th>
                      <th>Amount</th>
                      <th>D/C</th>
                      <th>Reporting WBS</th>
                      <th>Bank</th>
                      <th>Currency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAcitData.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedRows.some((r) => r.id === row.id)}
                            onChange={(e) =>
                              handleCheckboxChange(row, e.target.checked)
                            }
                          />
                        </td>
                        <td>{row.docno}</td>
                        <td>{row.docfy}</td>
                        <td>{row.doccc}</td>
                        <td>{row.docitem}</td>
                        <td>{row.amount}</td>
                        <td>{row.debitcredit}</td>
                        <td>{row.reportingWbs}</td>
                        <td>{row.bank}</td>
                        <td>{row.currencycode}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>No ACIT data found for selected filters.</p>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="alert alert-warning">
          No statement data available. Please go back and select a statement.
        </div>
      )}
    </div>
  );
};

export default PaymentReconciliation;
